import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  leadScoringRequestSchema,
  type LeadScoringRequest,
  type LeadScoringResponse,
} from "@io-spoke/contracts/leadScoring";
import { PageShell } from "@components/layout/PageShell";
import { Button } from "@components/ui/button";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@components/ui/empty";
import { Input } from "@components/ui/input";
import { FormChoiceSelect } from "@components/ui/choice-select";
import { Label } from "@components/ui/label";
import { Skeleton } from "@components/ui/skeleton";
import { Textarea } from "@components/ui/textarea";
import { scoreLead } from "../lib/leads";
import { LeadScoreCard } from "@components/leads/LeadScoreCard";
import { LeadNextActions } from "@components/leads/LeadNextActions";
import { AutoReplyComposer } from "@components/leads/AutoReplyComposer";

type LeadIntakePageProps = {
  navigate: (route: string) => void;
};

export function LeadIntakePage(_props: LeadIntakePageProps) {
  const [leadId] = useState(() => `lead-${crypto.randomUUID()}`);
  const [result, setResult] = useState<LeadScoringResponse | null>(null);
  const {
    register, control,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof leadScoringRequestSchema>, unknown, LeadScoringRequest>({
    resolver: zodResolver(leadScoringRequestSchema),
    defaultValues: {
      leadId,
      signals: {
        industry: "",
        intentSummary: "",
        urgency: "medium",
        notes: "",
      },
    },
  });
  const signals = watch("signals");
  const intentSummary = String(signals?.intentSummary ?? "");
  const hasLeadContext = Boolean(
    intentSummary.trim() ||
      String(signals?.industry ?? "").trim() ||
      String(signals?.notes ?? "").trim()
  );

  const {
    pending: scoreLeadContextPending,
    callback: scoreLeadContext,
  } = useAsyncCallback(async (payload: LeadScoringRequest) => {
    try {
      const scored = await scoreLead(payload);
      setResult(scored);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Lead scoring failed.";
      setError("root", { type: "server", message });
      setResult(null);
    }
  });

  return (
    <PageShell>
      <div className="flex flex-col gap-6">
        <header className="space-y-2">
          <div className="flex items-center gap-3">
            <SidebarTrigger />
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground/70">
              Instant buyer fit
            </p>
          </div>
          <h1 className="text-3xl font-semibold">Leads</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Qualify inbound leads with explainable scoring, then generate a tailored
            reply that still requires explicit confirmation before send.
          </p>
        </header>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <Card className="border-border/70">
            <CardHeader>
              <CardTitle className="text-base">Lead context</CardTitle>
              <CardDescription>
                Capture the minimum signal needed to make scoring useful.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleSubmit(scoreLeadContext)} noValidate>
              <div className="grid gap-4 md:grid-cols-1">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-industry">Industry</Label>
                  <Input
                    id="lead-industry"
                    {...register("signals.industry")}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lead-intent">Intent summary</Label>
                <Textarea
                  id="lead-intent"
                  {...register("signals.intentSummary")}
                  className="min-h-[110px]"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-company-size">Company size</Label>
                  <Input
                    id="lead-company-size"
                    inputMode="numeric"
                    {...register("signals.companySize", {
                      setValueAs: (value) => value === "" ? undefined : value,
                    })}
                    aria-invalid={Boolean(errors.signals?.companySize)}
                    aria-describedby={errors.signals?.companySize ? "lead-company-size-error" : undefined}
                  />
                  {errors.signals?.companySize ? <p id="lead-company-size-error" role="alert" className="text-xs text-destructive">{errors.signals.companySize.message}</p> : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-urgency">Urgency</Label>
                  <FormChoiceSelect
                    id="lead-urgency"
                    control={control} name="signals.urgency"
                   options={[{ value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" }]} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-interactions">Prior interactions</Label>
                  <Input
                    id="lead-interactions"
                    inputMode="numeric"
                    {...register("signals.priorInteractions", {
                      setValueAs: (value) => value === "" ? undefined : value,
                    })}
                    aria-invalid={Boolean(errors.signals?.priorInteractions)}
                    aria-describedby={errors.signals?.priorInteractions ? "lead-interactions-error" : undefined}
                  />
                  {errors.signals?.priorInteractions ? <p id="lead-interactions-error" role="alert" className="text-xs text-destructive">{errors.signals.priorInteractions.message}</p> : null}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lead-notes">Notes</Label>
                <Textarea
                  id="lead-notes"
                  {...register("signals.notes")}
                />
              </div>

              {errors.root?.message ? <div className="text-sm text-destructive" role="alert">{errors.root.message}</div> : null}

              <div className="flex flex-wrap items-center gap-2">
                <Button loading={scoreLeadContextPending} loadingLabel="Scoring lead…" type="submit" disabled={isSubmitting || !hasLeadContext}>
                  {isSubmitting ? "Scoring…" : "Score lead"}
                </Button>
                <div className="text-xs text-muted-foreground">
                  Scores are guidance, not guarantees.
                </div>
              </div>
              </form>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-6">
            {isSubmitting && !result ? (
              <Card className="border-border/70">
                <CardHeader>
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-64" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ) : null}

            {result ? (
              <>
                <LeadScoreCard result={result} />
                <LeadNextActions result={result} />
                <AutoReplyComposer
                  leadId={result.leadId}
                  band={result.band}
                  intentSummary={intentSummary}
                />
              </>
            ) : (
              <Card className="border-border/70">
                <CardHeader>
                  <CardTitle className="text-base">Score preview</CardTitle>
                  <CardDescription>
                    Run scoring to see rationale signals and next steps.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Empty className="border-border/60 bg-muted/20">
                    <EmptyHeader>
                      <EmptyTitle>No score yet</EmptyTitle>
                      <EmptyDescription>
                        Add lead context, then click “Score lead.”
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
